
/* File: frontend/src/modules/trading/dispatch_order/pages/TradingDispatchOrderDetailPage.js */

import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import tradingDispatchOrderService from "../services/tradingDispatchOrderService";

const styles = {
  page: {
    padding: "24px",
    background: "#f7f8fc",
    minHeight: "100%",
    fontFamily: "Inter, Arial, sans-serif",
    color: "#252b3b",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "16px",
    flexWrap: "wrap",
    marginBottom: "22px",
  },
  title: { margin: 0, fontSize: "25px", fontWeight: 700 },
  subtitle: { margin: "7px 0 0", color: "#747b8d", fontSize: "13px" },
  card: {
    background: "#fff",
    border: "1px solid #e8eaf2",
    borderRadius: "12px",
    padding: "20px",
    marginBottom: "18px",
  },
  sectionTitle: { margin: "0 0 18px", fontSize: "16px", fontWeight: 700 },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))",
    gap: "20px",
  },
  label: {
    color: "#747b8d",
    fontSize: "11px",
    fontWeight: 700,
    textTransform: "uppercase",
    marginBottom: "7px",
    letterSpacing: ".3px",
  },
  value: {
    fontSize: "14px",
    fontWeight: 600,
    overflowWrap: "anywhere",
    whiteSpace: "pre-wrap",
  },
  button: {
    border: 0,
    borderRadius: "8px",
    padding: "11px 17px",
    background: "#6254d9",
    color: "#fff",
    fontWeight: 600,
    cursor: "pointer",
  },
  secondaryButton: {
    border: "1px solid #dfe2eb",
    borderRadius: "8px",
    padding: "10px 14px",
    background: "#fff",
    color: "#374151",
    fontWeight: 600,
    cursor: "pointer",
  },
  dangerButton: {
    border: "1px solid #f2caca",
    borderRadius: "8px",
    padding: "10px 14px",
    background: "#fff",
    color: "#b42318",
    fontWeight: 600,
    cursor: "pointer",
  },
  badge: {
    display: "inline-block",
    borderRadius: "20px",
    padding: "6px 10px",
    fontSize: "11px",
    fontWeight: 700,
    whiteSpace: "nowrap",
  },
  tableWrap: { width: "100%", overflowX: "auto" },
  table: { width: "100%", borderCollapse: "collapse", minWidth: "760px" },
  th: {
    textAlign: "left",
    padding: "12px",
    background: "#f7f8fc",
    color: "#70778a",
    fontSize: "11px",
    textTransform: "uppercase",
    borderBottom: "1px solid #e8eaf2",
    whiteSpace: "nowrap",
  },
  td: {
    padding: "13px 12px",
    fontSize: "13px",
    borderBottom: "1px solid #f0f1f6",
    verticalAlign: "top",
  },
  error: {
    padding: "12px 14px",
    background: "#fff0f0",
    color: "#b42318",
    borderRadius: "8px",
    fontSize: "13px",
    marginBottom: "16px",
  },
  info: {
    padding: "12px 14px",
    background: "#f0f5ff",
    color: "#244a91",
    borderRadius: "8px",
    fontSize: "13px",
    marginBottom: "16px",
  },
  muted: { color: "#858b9b", fontSize: "13px" },
  textarea: {
    width: "100%",
    boxSizing: "border-box",
    padding: "12px",
    border: "1px solid #dfe2eb",
    borderRadius: "8px",
    font: "inherit",
    fontSize: "13px",
    resize: "vertical",
  },
};

const getResponseData = (response) =>
  response?.data?.data ?? response?.data ?? {};

const getErrorMessage = (err, fallback) =>
  err?.response?.data?.message ||
  err?.response?.data?.error ||
  err?.message ||
  fallback;

const formatStatus = (value) =>
  String(value || "UNKNOWN").toUpperCase().replace(/_/g, " ");

const formatDate = (value) => {
  if (!value) return "—";

  const raw = String(value).slice(0, 10);
  const parts = raw.split("-");

  return parts.length === 3
    ? `${parts[2]}-${parts[1]}-${parts[0]}`
    : raw;
};

const formatQuantity = (value) => {
  if (value === null || value === undefined || value === "") return "—";

  const number = Number(value);

  return Number.isFinite(number)
    ? number.toLocaleString("en-IN", { maximumFractionDigits: 3 })
    : "—";
};

const statusColors = {
  DRAFT: { background: "#efedff", color: "#5548cb" },
  PENDING_APPROVAL: { background: "#fff4df", color: "#945d00" },
  WAITING_FOR_APPROVAL: { background: "#fff4df", color: "#945d00" },
  APPROVED: { background: "#e8f8ee", color: "#187443" },
  REJECTED: { background: "#fff0f0", color: "#b42318" },
  CANCELLED: { background: "#edf0f5", color: "#596176" },
};

function Field({ label, value }) {
  return (
    <div>
      <div style={styles.label}>{label}</div>
      <div style={styles.value}>
        {value === null || value === undefined || value === ""
          ? "—"
          : value}
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const code = String(status || "UNKNOWN").toUpperCase();

  return (
    <span
      style={{
        ...styles.badge,
        ...(statusColors[code] || {
          background: "#edf0f5",
          color: "#596176",
        }),
      }}
    >
      {formatStatus(code)}
    </span>
  );
}

export default function TradingDispatchOrderDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [dispatchOrder, setDispatchOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [remarks, setRemarks] = useState("");
  const [rejectionReason, setRejectionReason] = useState("");
  const [showReject, setShowReject] = useState(false);

  const loadDispatchOrder = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const response =
        await tradingDispatchOrderService.getDispatchOrder(id);

      const body = getResponseData(response);
      const record = body?.dispatch_order || body;

      if (!record || !record.id) {
        throw new Error(
          "Dispatch Order details were not returned by the API."
        );
      }

      setDispatchOrder(record);
    } catch (err) {
      setDispatchOrder(null);
      setError(
        getErrorMessage(err, "Unable to load Dispatch Order details.")
      );
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadDispatchOrder();
  }, [loadDispatchOrder]);

  const submitForApproval = async () => {
    if (!dispatchOrder) return;

    if (
      !window.confirm(
        `Submit Dispatch Order ${
          dispatchOrder.dispatch_order_no || dispatchOrder.id
        } for approval?`
      )
    ) {
      return;
    }

    setActionLoading(true);
    setError("");
    setInfo("");

    try {
      await tradingDispatchOrderService.submitForApproval(
        id,
        { remarks: remarks.trim() || undefined }
      );

      setRemarks("");
      setInfo("Dispatch Order submitted for approval.");
      await loadDispatchOrder();
    } catch (err) {
      setError(
        getErrorMessage(err, "Unable to submit Dispatch Order for approval.")
      );
    } finally {
      setActionLoading(false);
    }
  };

  const approveDispatchOrder = async () => {
    if (!dispatchOrder) return;

    if (
      !window.confirm(
        `Approve Dispatch Order ${
          dispatchOrder.dispatch_order_no || dispatchOrder.id
        }?`
      )
    ) {
      return;
    }

    setActionLoading(true);
    setError("");
    setInfo("");

    try {
      await tradingDispatchOrderService.approveDispatchOrder(id);

      setInfo("Dispatch Order approved.");
      await loadDispatchOrder();
    } catch (err) {
      setError(
        getErrorMessage(err, "Unable to approve Dispatch Order.")
      );
    } finally {
      setActionLoading(false);
    }
  };

  const rejectDispatchOrder = async () => {
    if (!rejectionReason.trim()) {
      setError("Please enter a rejection reason.");
      return;
    }

    if (!dispatchOrder) return;

    if (
      !window.confirm(
        `Reject Dispatch Order ${
          dispatchOrder.dispatch_order_no || dispatchOrder.id
        }?`
      )
    ) {
      return;
    }

    setActionLoading(true);
    setError("");
    setInfo("");

    try {
      await tradingDispatchOrderService.rejectDispatchOrder(
        id,
        rejectionReason.trim()
      );

      setRejectionReason("");
      setShowReject(false);
      setInfo("Dispatch Order rejected.");
      await loadDispatchOrder();
    } catch (err) {
      setError(
        getErrorMessage(err, "Unable to reject Dispatch Order.")
      );
    } finally {
      setActionLoading(false);
    }
  };

  if (loading && !dispatchOrder) {
    return (
      <div style={styles.page}>
        <p style={styles.muted}>Loading Dispatch Order details...</p>
      </div>
    );
  }

  if (!dispatchOrder) {
    return (
      <div style={styles.page}>
        <div style={styles.header}>
          <h1 style={styles.title}>Dispatch Order Details</h1>
          <button
            type="button"
            style={styles.secondaryButton}
            onClick={() => navigate("/trading/dispatch-orders")}
          >
            Back to Dispatch Orders
          </button>
        </div>

        {error && (
          <div role="alert" style={styles.error}>
            {error}
          </div>
        )}

        <button
          type="button"
          style={styles.button}
          onClick={loadDispatchOrder}
        >
          Retry
        </button>
      </div>
    );
  }

  const status = String(
    dispatchOrder.status_code || "DRAFT"
  ).toUpperCase();

  const canEdit = status === "DRAFT";
  const canSubmit = status === "DRAFT";
  const canApprove = [
    "PENDING_APPROVAL",
    "WAITING_FOR_APPROVAL",
  ].includes(status);
  const canReject = canApprove;

  const items = Array.isArray(dispatchOrder.items)
    ? dispatchOrder.items
    : [];

  const totalPlannedQuantity = items.reduce(
    (total, item) =>
      total + Number(item.planned_dispatch_quantity || 0),
    0
  );

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>
            {dispatchOrder.dispatch_order_no ||
              `Dispatch Order ${dispatchOrder.id}`}
          </h1>
          <p style={styles.subtitle}>
            Trading Dispatch Order details and planned quantities
          </p>
          <div style={{ marginTop: "10px" }}>
            <StatusBadge status={status} />
          </div>
        </div>

        <div
          style={{
            display: "flex",
            gap: "10px",
            flexWrap: "wrap",
          }}
        >
          <button
            type="button"
            style={styles.secondaryButton}
            onClick={() => navigate("/trading/dispatch-orders")}
          >
            Back to List
          </button>

          <button
            type="button"
            style={styles.secondaryButton}
            onClick={loadDispatchOrder}
            disabled={loading || actionLoading}
          >
            Refresh
          </button>

          {canEdit && (
            <button
              type="button"
              style={styles.secondaryButton}
              onClick={() =>
                navigate(`/trading/dispatch-orders/${id}/edit`)
              }
            >
              Edit
            </button>
          )}

          {canSubmit && (
            <button
              type="button"
              style={styles.button}
              onClick={submitForApproval}
              disabled={actionLoading}
            >
              {actionLoading ? "Processing..." : "Submit For Approval"}
            </button>
          )}

          {/*
          {canApprove && (
            <button
              type="button"
              style={styles.button}
              onClick={approveDispatchOrder}
              disabled={actionLoading}
            >
            
            
              {actionLoading ? "Processing..." : "Approve"}
            </button>
          )}

          {canReject && (
            <button
              type="button"
              style={styles.dangerButton}
              onClick={() => setShowReject((current) => !current)}
              disabled={actionLoading}
            >
              Reject
            </button>

          )}
          */}  
        </div>
      </div>

      {error && (
        <div role="alert" style={styles.error}>
          {error}
        </div>
      )}

      {info && (
        <div role="status" style={styles.info}>
          {info}
        </div>
      )}

      {showReject && (
        <section style={styles.card}>
          <h2 style={styles.sectionTitle}>Reject Dispatch Order</h2>

          <label style={styles.label} htmlFor="dispatch-rejection-reason">
            Rejection Reason *
          </label>

          <textarea
            id="dispatch-rejection-reason"
            rows={3}
            value={rejectionReason}
            onChange={(event) => setRejectionReason(event.target.value)}
            placeholder="Enter the reason for rejection"
            style={styles.textarea}
          />

          <div
            style={{
              display: "flex",
              gap: "10px",
              marginTop: "12px",
            }}
          >
            <button
              type="button"
              style={styles.secondaryButton}
              onClick={() => setShowReject(false)}
              disabled={actionLoading}
            >
              Cancel
            </button>

            <button
              type="button"
              style={{
                ...styles.dangerButton,
                background: "#b42318",
                color: "#fff",
              }}
              onClick={rejectDispatchOrder}
              disabled={actionLoading}
            >
              {actionLoading ? "Rejecting..." : "Confirm Rejection"}
            </button>
          </div>
        </section>
      )}

      <section style={styles.card}>
        <h2 style={styles.sectionTitle}>Dispatch Order Overview</h2>

        <div style={styles.grid}>
          <Field
            label="Dispatch Order No."
            value={dispatchOrder.dispatch_order_no}
          />
          <Field
            label="Dispatch Order Date"
            value={formatDate(dispatchOrder.dispatch_order_date)}
          />
          <Field
            label="Planned Dispatch Date"
            value={formatDate(dispatchOrder.planned_dispatch_date)}
          />
          <Field
            label="Sales Order No."
            value={dispatchOrder.sales_order_no}
          />
          <Field
            label="Sales Order ID"
            value={dispatchOrder.sales_order_id}
          />
          <Field
            label="Customer"
            value={
              dispatchOrder.customer_name ||
              (dispatchOrder.customer_id
                ? `Customer #${dispatchOrder.customer_id}`
                : null)
            }
          />
          <Field
            label="Customer ID"
            value={dispatchOrder.customer_id}
          />
          <Field
            label="Department ID"
            value={dispatchOrder.department_id}
          />
          <Field
            label="Total Planned Quantity"
            value={formatQuantity(totalPlannedQuantity)}
          />
        </div>
      </section>

      <section style={styles.card}>
        <h2 style={styles.sectionTitle}>Dispatch Items ({items.length})</h2>

        <div style={styles.tableWrap}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Line</th>
                <th style={styles.th}>Item Description</th>
                <th style={styles.th}>Sales Order Item ID</th>
                <th style={styles.th}>Ordered Qty.</th>
                <th style={styles.th}>Dispatched Snapshot</th>
                <th style={styles.th}>Remaining Snapshot</th>
                <th style={styles.th}>Planned Dispatch Qty.</th>
              </tr>
            </thead>

            <tbody>
              {items.length === 0 ? (
                <tr>
                  <td
                    colSpan={7}
                    style={{
                      ...styles.td,
                      textAlign: "center",
                      color: "#858b9b",
                    }}
                  >
                    No dispatch items returned by the API.
                  </td>
                </tr>
              ) : (
                items.map((item, index) => (
                  <tr key={item.id || item.sales_order_item_id || index}>
                    <td style={styles.td}>
                      {item.item_seq_no ?? index + 1}
                    </td>
                    <td style={{ ...styles.td, minWidth: "180px" }}>
                      {item.item_description || "—"}
                    </td>
                    <td style={styles.td}>
                      {item.sales_order_item_id ?? "—"}
                    </td>
                    <td style={styles.td}>
                      {formatQuantity(item.ordered_quantity)}
                    </td>
                    <td style={styles.td}>
                      {formatQuantity(item.dispatched_quantity_snapshot)}
                    </td>
                    <td style={styles.td}>
                      {formatQuantity(item.remaining_quantity_snapshot)}
                    </td>
                    <td style={{ ...styles.td, fontWeight: 700 }}>
                      {formatQuantity(item.planned_dispatch_quantity)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      <section style={styles.card}>
        <h2 style={styles.sectionTitle}>Delivery & Transport Details</h2>

        <div style={styles.grid}>
          <Field
            label="Delivery Address"
            value={dispatchOrder.delivery_address}
          />
          <Field
            label="Transporter Booking Required"
            value={
              dispatchOrder.transporter_booking_required ? "Yes" : "No"
            }
          />
          <Field
            label="Transporter Business Partner ID"
            value={dispatchOrder.transporter_bp_id}
          />
          <Field
            label="Planned Vehicle Number"
            value={dispatchOrder.planned_vehicle_number}
          />
          <Field
            label="Transport Request ID"
            value={dispatchOrder.transport_request_id}
          />
          <Field
            label="Delivery Instructions"
            value={dispatchOrder.delivery_instructions}
          />
          <Field
            label="Remarks"
            value={dispatchOrder.remarks}
          />
        </div>
      </section>

      <section style={styles.card}>
        <h2 style={styles.sectionTitle}>Approval Information</h2>

        <div style={styles.grid}>
          <Field
            label="Approved By"
            value={dispatchOrder.approved_by}
          />
          <Field
            label="Approved At"
            value={formatDate(dispatchOrder.approved_at)}
          />
          <Field
            label="Rejected By"
            value={dispatchOrder.rejected_by}
          />
          <Field
            label="Rejected At"
            value={formatDate(dispatchOrder.rejected_at)}
          />
          <Field
            label="Rejection Reason"
            value={dispatchOrder.rejection_reason}
          />
        </div>
      </section>

      {canSubmit && (
        <section style={styles.card}>
          <h2 style={styles.sectionTitle}>Submit for Approval</h2>

          <label style={styles.label} htmlFor="dispatch-approval-remarks">
            Remarks (optional)
          </label>

          <textarea
            id="dispatch-approval-remarks"
            rows={3}
            value={remarks}
            onChange={(event) => setRemarks(event.target.value)}
            placeholder="Add remarks for the approver"
            style={styles.textarea}
          />

          <button
            type="button"
            style={{ ...styles.button, marginTop: "12px" }}
            onClick={submitForApproval}
            disabled={actionLoading}
          >
            {actionLoading ? "Submitting..." : "Submit For Approval"}
          </button>
        </section>
      )}
    </div>
  );
}
